/* eslint-disable  */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { bindActionCreators } from "redux";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import {
  FileText,
  ClipboardList,
  BookOpen,
  CheckCircle,
  Clock,
  CreditCard,
  XCircle,
  ChevronRight,
  Send,
} from "lucide-react";
import axiosInstance from "../services/Axios/axios";

// Components & Actions
import Loader from "../Components/Loader";
import RenderLoanPage from "./RenderLoanPage";
import { GetUserProfile } from "../services/Actions/authActions";
import {
  dataActionCreators,
  messageActionCreators,
} from "../services/Actions";
import NotFoundComponent from "../Components/NotFoundComponent";
import { format } from "timeago.js";

function LoansDashboard() {
  const history = useHistory();
  const dispatch = useDispatch();

  const { GetAppliedLoans, GetTotalLoanStats, ResetDataResponse } =
    bindActionCreators(dataActionCreators, dispatch);
  const { GetLoanOfficersMessagesUnread } = bindActionCreators(
    messageActionCreators,
    dispatch
  );

  const data = useSelector((state) => state?.data);
  const { totalLoanStats, response, isLoading } = data;

  const [processedSettlementsCount, setProcessedSettlementsCount] = useState(0);

  useEffect(() => {
    dispatch(GetUserProfile());
    GetAppliedLoans(true, 1, 5, "submitted");
    GetTotalLoanStats();
    GetLoanOfficersMessagesUnread();

    axiosInstance
      .get("/settlement?status=processed")
      .then((res) => {
        const all = res.data || [];
        setProcessedSettlementsCount(all.filter((s) => s.settlement_type !== "mart").length);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    }
  }, [response?.state]);

  return (
    <RenderLoanPage title="Loans Overview">
      {isLoading && <Loader />}

      {/* STATS GRID */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <DashboardCard
          title="Total Applications"
          value={totalLoanStats?.totalLoans}
          icon={<FileText size={24} />}
          link="/loans/application"
          color="bg-blue-600"

        />
        <DashboardCard
          title="Submitted Loans"
          value={totalLoanStats?.totalSubmited}
          icon={<ClipboardList size={24} />}
          link="/loans/application"
          color="bg-emerald-600"
        />
        <DashboardCard
          title="Loan Booked"
          value={totalLoanStats?.totalBooked}
          icon={<BookOpen size={24} />}
          link="/loans/booked"
          color="bg-indigo-600"
        />
        <DashboardCard
          title="Loan Authorized"
          value={totalLoanStats?.totalAuthorized}
          icon={<CheckCircle size={24} />}
          link="/loans/authorized"
          color="bg-teal-600"
        />
        <DashboardCard
          title="Loan Initiated"
          value={totalLoanStats?.totalInitiated}
          icon={<Clock size={24} />}
          link="/loans/initiated"
          color="bg-amber-500"
        />
        <DashboardCard
          title="Paid Loans"
          value={totalLoanStats?.totalPaid}
          icon={<CreditCard size={24} />}
          link="/loans/paid"
          color="bg-purple-600"
        />
        <DashboardCard
          title="Rejected Loans"
          value={totalLoanStats?.totalRejected}
          icon={<XCircle size={24} />}
          link="/loans/rejected"
          color="bg-rose-600"
        />
        <DashboardCard
          title="Processed Settlements"
          value={processedSettlementsCount}
          icon={<Send size={24} />}
          link="/loans/sent-settlements"
          color="bg-cyan-600"
        />
      </section>

      {/* RECENT LOAN REQUESTS TABLE */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-slate-50 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h2 className="text-md font-bold text-slate-800 dark:text-slate-100">
              Recent Loan Applications
            </h2>
          </div>
          <Link
            to="/loans/application"
            className="text-indigo-600 font-semibold text-sm hover:underline"
          >
            View All
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-gray-700/50">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Member Info
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Loan Amount
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Sector
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {data?.appliedLoans?.length > 0 ? (
                data?.appliedLoans.map((loan, index) => (
                  <tr
                    key={index}
                    className="hover:bg-slate-50/80 dark:hover:bg-gray-700/80 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          className="w-10 h-10 rounded-full border border-slate-200 object-cover shadow-sm"
                          src={
                            loan?.user?.profile_image ||
                            "/images/dev/avatar1.png"
                          }
                          alt="Profile"
                        />
                        <div>
                          <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">
                            {loan?.user?.name}
                          </p>
                          <p className="text-xs font-medium text-slate-500">
                            {loan?.staff_id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                        &#8373;{loan?.loanAmount}
                      </span>
                      <p className="text-xs text-slate-400 mt-1">
                        MD: {loan?.monthlyDeduction}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">
                        {moment(loan?.created_at).format("MMM DD, YYYY")}
                      </p>
                      <p className="text-xs text-slate-400">
                        {format(loan?.created_at)}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-100">
                        {loan?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${loan?.sector?.split(":")[0] === "Northern"
                          ? "bg-red-50 text-red-700 border-red-100"
                          : "bg-blue-50 text-blue-700 border-blue-100"
                          }`}
                      >
                        {loan?.sector?.split(":")[0]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() =>
                          history.push({
                            pathname: "/loans/loan-detailed",
                            state: { loan_id: loan._id },
                          })
                        }
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-slate-400 italic"
                  >
                    No loan applications available at the moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ToastContainer autoClose={3000} />
    </RenderLoanPage>
  );
}

/**
 * Modern Stat Card Component
 */
const DashboardCard = ({ title, value, icon, link, color }) => (
  <div className="relative overflow-hidden bg-white dark:bg-gray-800 p-3 rounded-2xl border border-slate-100 dark:border-gray-700 shadow-sm group hover:shadow-md transition-all duration-300">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-full text-white shadow-lg ${color}`}>
        {icon}
      </div>
      <Link
        to={link}
        className="text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-indigo-600 transition-colors flex items-center gap-1"
      >
        View More <ChevronRight size={12} />
      </Link>
    </div>

    <div>
      <p className="text-sm font-medium text-slate-500">{title}</p>
      <h3 className="text-2xl font-black text-slate-800 mt-1">
        {value?.toLocaleString() || 0}
      </h3>
    </div>

    {/* Subtle Decorative Circle */}
    <div
      className={`absolute -right-4 -bottom-4 w-16 h-16 rounded-full opacity-5 group-hover:scale-150 transition-transform duration-500 ${color}`}
    ></div>
  </div>
);

export default LoansDashboard;
